/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { api } from '../../api';
import { CustomerListFilter } from './CustomerListFilter';
import type { Quote, QuoteSnapshot } from '../../api/areas/commerce';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, FilterBar, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { DomainLink } from '../../components/Ids';
import { KeyValue, type KeyValueRow } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { productName, ProductOperation } from './ProductItem';
import { fractionToPercent, ruleValue } from './PricingPage';
import {
  Amount,
  cellLink,
  clearParams,
  CustomerRef,
  FilterSelect,
  FilterText,
  Label,
  mutedSmall,
  OrderRef,
  Pager,
  priceBookPath,
  promotionPath,
  QuoteRef,
  RefreshButton,
  ResetFilters,
  RichText,
  setParam,
  useKeyset,
  When,
} from './shared';

const OPERATIONS = ['REGISTER', 'RENEW', 'TRANSFER', 'RESTORE', 'REDEMPTION'] as const;
const FILTER_KEYS = ['domain', 'operation', 'customer_id'] as const;

function QuoteState({ q }: { q: Quote }) {
  const { t } = useTranslation('commerce');
  if (q.order_id) return <Badge tone="ok">{t('quotes.state.ORDERED')}</Badge>;
  if (q.expires_at < new Date().toISOString()) return <Badge tone="neutral">{t('quotes.state.EXPIRED')}</Badge>;
  return <Badge tone="pending">{t('quotes.state.OPEN')}</Badge>;
}

/** `/quotes`: what each customer was quoted, newest first. */
export function QuotesPage() {
  const { t } = useTranslation('commerce');
  useDocumentTitle(t('quotes.title'));
  const [params, setParams] = useSearchParams();
  const domain = params.get('domain') ?? '';
  const operation = params.get('operation') ?? '';
  const customer = params.get('customer_id') ?? '';
  const pager = useKeyset(params.toString());
  const result = useApi(
    (signal) => api.listQuotes({ domain: domain || undefined, operation: operation || undefined, customer_id: customer || undefined, limit: 50, before: pager.before }, { signal }),
    [domain, operation, customer, pager.before],
  );
  const rows = result.status === 'success' ? result.data.items : [];

  const columns: Column<Quote>[] = [
    { key: 'domain', header: t('quotes.col.domain'), cell: (q) => <QuoteRef id={q.id} domain={productName(q)} sub={q.product_kind === 'SERVICE' ? q.plan_code : `.${q.tld}`} /> },
    {
      key: 'operation',
      header: t('quotes.col.operation'),
      className: 'whitespace-nowrap',
      cell: (q) => (
        <span className="inline-flex items-center gap-1.5">
          <ProductOperation item={q} />
          {q.premium ? (
            <Badge tone="info" icon={false}>
              {t('common.premium')}
            </Badge>
          ) : null}
        </span>
      ),
    },
    { key: 'customer', header: t('quotes.col.customer'), cell: (q) => <CustomerRef id={q.customer_id} name={q.customer_name} /> },
    { key: 'subtotal', header: t('quotes.col.subtotal'), numeric: true, cell: (q) => <Amount amount={q.subtotal} currency={q.currency} /> },
    { key: 'tax', header: t('quotes.col.tax'), numeric: true, cell: (q) => <Amount amount={q.tax} currency={q.currency} /> },
    { key: 'total', header: t('quotes.col.total'), numeric: true, cell: (q) => <Amount amount={q.total} currency={q.currency} className="font-semibold" /> },
    {
      key: 'state',
      header: t('quotes.col.state'),
      cell: (q) => (
        <span className="inline-flex flex-col gap-0.5">
          <QuoteState q={q} />
          {q.order_id ? <OrderRef id={q.order_id} domain={q.order_domain} count={q.order_item_count} at={q.order_placed_at} /> : null}
        </span>
      ),
    },
    { key: 'created', header: t('quotes.col.created'), cell: (q) => <When at={q.created_at} /> },
  ];

  return (
    <>
      <PageHeader title={t('quotes.title')} crumbs={[{ label: t('crumb') }]} subtitle={<RichText k="quotes.subtitle" />} actions={<RefreshButton onClick={result.reload} />} />
      <FilterBar actions={<ResetFilters show={Boolean(domain || operation || customer)} onReset={() => setParams(clearParams(params, FILTER_KEYS))} />}>
        <FilterText label={t('quotes.domainFilter')} value={domain} onChange={(v) => setParams(setParam(params, 'domain', v.toLowerCase()))} placeholder={t('quotes.domainPlaceholder')} />
        <FilterSelect label={t('common.operation')} value={operation} options={OPERATIONS} group="operation" onChange={(v) => setParams(setParam(params, 'operation', v))} />
        <CustomerListFilter value={customer} name={rows[0]?.customer_name} onChange={(v) => setParams(setParam(params, 'customer_id', v))} />
      </FilterBar>
      <Section flush>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={5} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} title={t('quotes.loadError')} />
          </div>
        ) : null}
        {result.status === 'success' ? (
          <>
            <DataTable columns={columns} rows={rows} rowKey={(q) => q.id} empty={<EmptyState title={t('quotes.empty')} />} />
            <Pager cursors={pager.cursors} next={result.data.next_before} onNewer={pager.newer} onOlder={pager.older} />
          </>
        ) : null}
      </Section>
    </>
  );
}

/** The price components the quote froze, in the order they were applied. */
function Breakdown({ snapshot: s, currency }: { snapshot: QuoteSnapshot; currency: string }) {
  const { t, i18n } = useTranslation('commerce');
  if (s.kind === 'SERVICE') return <DataTable columns={[
    { key: 'description', header: t('common.product'), cell: (line) => i18n.language.startsWith('en') ? line.description_en : line.description_vi },
    { key: 'amount', header: t('common.subtotal'), numeric: true, cell: (line) => <Amount amount={line.amount} currency={currency} /> },
    { key: 'tax', header: t('common.tax'), numeric: true, cell: (line) => <Amount amount={line.tax} currency={currency} /> },
    { key: 'total', header: t('common.total'), numeric: true, cell: (line) => <Amount amount={line.total} currency={currency} /> },
  ] satisfies Column<NonNullable<QuoteSnapshot['lines']>[number]>[]} rows={s.lines ?? []} rowKey={(line) => `${line.kind}:${line.code}`} />;
  const cost = s.registry_cost;
  const rule = s.price_rule;
  const promo = s.promotion;
  const rows: KeyValueRow[] = [
    cost?.amount
      ? [
          cost.source === 'REGISTRY_PREMIUM' ? t('quote.bd.premiumCost') : t('quote.bd.registryCost'),
          <span key="rc">
            <Amount amount={cost.amount} currency={cost.currency} />
            {cost.version ? <span className={mutedSmall}> {t('quote.bd.version', { n: cost.version })}</span> : null}
          </span>,
        ]
      : null,
    s.fx?.rate && s.registry_cost_in_billing_currency
      ? [
          t('quote.bd.converted'),
          <span key="fx">
            <Amount amount={s.registry_cost_in_billing_currency} currency={currency} />
            <span className={`${mutedSmall} block`}>
              {t('quote.bd.rate', { base: cost?.currency ?? '', rate: s.fx.rate })} · {s.fx.source ?? ''}
            </span>
          </span>,
        ]
      : null,
    s.price_book?.code
      ? [
          t('quote.bd.priceBook'),
          s.price_book.id ? (
            <Link key="pb" to={priceBookPath(s.price_book.id)} className={`${cellLink} font-mono`}>
              {s.price_book.code}
            </Link>
          ) : (
            <code key="pb">{s.price_book.code}</code>
          ),
        ]
      : null,
    rule?.type
      ? [
          t('quote.bd.rule'),
          <span key="rule">
            <Label group="ruleType" value={rule.type} /> {rule.value ? ruleValue({ rule_type: rule.type, value: rule.value }, currency) : null}
            {rule.version ? <span className={mutedSmall}> {t('quote.bd.version', { n: rule.version })}</span> : null}
          </span>,
        ]
      : null,
    s.base ? [t('quote.bd.base'), <Amount key="base" amount={s.base} currency={currency} />] : null,
    [
      t('quote.bd.promotion'),
      promo?.code ? (
        <span key="promo" className="inline-flex flex-wrap items-baseline gap-1.5">
          {promo.promotion_id ? (
            <Link to={promotionPath(promo.promotion_id)} className={`${cellLink} font-mono`}>
              {promo.code}
            </Link>
          ) : (
            <code>{promo.code}</code>
          )}
          {promo.discount ? (
            <span>
              − <Amount amount={promo.discount} currency={currency} />
            </span>
          ) : null}
        </span>
      ) : (
        <span key="promo" className="text-muted-foreground">
          {t('quote.bd.noPromotion')}
        </span>
      ),
    ],
    s.subtotal ? [t('common.subtotal'), <Amount key="sub" amount={s.subtotal} currency={currency} />] : null,
    s.tax_amount
      ? [
          s.tax?.tax_code ? t('quote.bd.tax', { code: s.tax.tax_code, rate: fractionToPercent(s.tax.rate ?? '0') }) : t('common.tax'),
          <Amount key="tax" amount={s.tax_amount} currency={currency} />,
        ]
      : null,
    s.total ? [t('common.total'), <Amount key="total" amount={s.total} currency={currency} className="text-base font-semibold" />] : null,
  ];
  return <KeyValue columns={2} rows={rows} />;
}

/** `/quotes/:quoteId`: one quote and how its price was built. */
export function QuoteDetailPage() {
  const { t } = useTranslation('commerce');
  const { quoteId = '' } = useParams();
  useDocumentTitle(t('quote.title'));
  const result = useApi((signal) => api.getQuote(quoteId, { signal }), [quoteId]);
  const crumbs = [{ label: t('crumb') }, { label: t('quotes.title'), to: '/quotes' }];
  if (result.status === 'loading') return <Loading rows={4} />;
  if (result.status === 'error') {
    return (
      <>
        <PageHeader title={t('quote.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={result.reload} title={t('quote.loadError')} />
      </>
    );
  }
  const q = result.data;
  return (
    <>
      <PageHeader title={t('quote.titleFor', { domain: productName(q) })} crumbs={[...crumbs, { label: productName(q) }]} actions={<RefreshButton onClick={result.reload} />} />
      <div className="flex flex-col gap-6">
        <Section>
          <KeyValue
            columns={3}
            rows={[
              [t('quote.field.state'), <QuoteState key="s" q={q} />],
              q.product_kind === 'DOMAIN' ? [t('common.domain'), <DomainLink key="d" keyOrName={q.domain_name} />] : [t('common.product'), productName(q)],
              q.product_kind === 'SERVICE' ? [t('common.plan'), q.plan_code] : null,
              q.product_kind === 'DOMAIN' ? [t('quote.field.tld'), <span key="t">.{q.tld}</span>] : null,
              [t('common.operation'), <span key="op"><ProductOperation item={q} longYears /></span>],
              q.product_kind === 'DOMAIN' ? [t('quote.field.premium'), q.premium ? t('common.yes') : t('common.no')] : null,
              [t('common.customer'), <CustomerRef key="c" id={q.customer_id} name={q.customer_name} />],
              [t('common.order'), <OrderRef key="o" id={q.order_id} domain={q.order_domain} count={q.order_item_count} at={q.order_placed_at} />],
              [t('common.subtotal'), <Amount key="st" amount={q.subtotal} currency={q.currency} />],
              [t('common.tax'), <Amount key="tx" amount={q.tax} currency={q.currency} />],
              [t('common.total'), <Amount key="tt" amount={q.total} currency={q.currency} className="text-base font-semibold" />],
              [t('common.created'), formatDateTime(q.created_at)],
              [t('quote.field.expires'), formatDateTime(q.expires_at)],
              [t('quote.field.id'), <CopyText key="id" value={q.id} />],
            ]}
          />
        </Section>
        <Section title={t('quote.breakdown')}>
          <Breakdown snapshot={q.snapshot} currency={q.currency} />
        </Section>
        <Callout tone="info">{t('quote.immutable')}</Callout>
      </div>
    </>
  );
}
